import { CopyIdButton, type PanelSpan } from '@/components/shared';
import { formatBoolean, formatDateTime, formatNullableValue, formatShortId } from '../format';
import type { BackofficeOrganizationDetail } from '../types';
import { OrganizationDetailPanel } from './OrganizationDetailPanelGrid';

export interface OrganizationDetailsPanelProps {
  organization: BackofficeOrganizationDetail;
  panelId?: string | undefined;
  currentSpan?: PanelSpan | undefined;
}

function DetailRow({
  label,
  value,
  copyValue,
  copyLabel,
}: {
  label: string;
  value: string;
  copyValue?: string;
  copyLabel?: string;
}) {
  return (
    <div className="grid gap-1 border-b border-neutral-100 py-3 last:border-b-0 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-4">
      <dt className="font-semibold text-neutral-900">{label}</dt>
      <dd className="min-w-0 text-neutral-700">
        {copyValue && copyLabel ? (
          <div className="group inline-flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate">{value}</span>
            <CopyIdButton value={copyValue} label={copyLabel} />
          </div>
        ) : (
          <span>{value}</span>
        )}
      </dd>
    </div>
  );
}

export function OrganizationDetailsPanel({
  organization,
  panelId,
  currentSpan,
}: OrganizationDetailsPanelProps) {
  const detailItems = [
    {
      id: 'id',
      label: 'ID',
      content: (
        <DetailRow
          key="id"
          label="ID"
          value={formatShortId(organization.id)}
          copyValue={organization.id}
          copyLabel="Copy organization ID"
        />
      ),
    },
    {
      id: 'name',
      label: 'Name',
      content: (
        <DetailRow
          key="name"
          label="Name"
          value={organization.name}
          copyValue={organization.name}
          copyLabel="Copy organization name"
        />
      ),
    },
    {
      id: 'slug',
      label: 'Slug',
      content: <DetailRow key="slug" label="Slug" value={formatNullableValue(organization.slug)} />,
    },
    {
      id: 'avatar-url',
      label: 'Avatar URL',
      content: (
        <DetailRow
          key="avatar-url"
          label="Avatar URL"
          value={formatNullableValue(organization.avatarUrl)}
        />
      ),
    },
    {
      id: 'anonymous',
      label: 'Anonymous',
      content: (
        <DetailRow
          key="anonymous"
          label="Anonymous"
          value={formatBoolean(organization.isAnonymous)}
        />
      ),
    },
    {
      id: 'pricing-tier',
      label: 'Pricing tier',
      content: (
        <DetailRow key="pricing-tier" label="Pricing tier" value={organization.pricingTierName} />
      ),
    },
    {
      id: 'pricing-tier-id',
      label: 'Pricing tier ID',
      content: (
        <DetailRow key="pricing-tier-id" label="Pricing tier ID" value={organization.pricingTier} />
      ),
    },
    {
      id: 'member-count',
      label: 'Member count',
      content: (
        <DetailRow
          key="member-count"
          label="Member count"
          value={String(organization.memberCount)}
        />
      ),
    },
    {
      id: 'interview-balance',
      label: 'Interview balance',
      content: (
        <DetailRow
          key="interview-balance"
          label="Interview balance"
          value={
            organization.interviewBalance === null
              ? 'Not available'
              : String(organization.interviewBalance)
          }
        />
      ),
    },
    {
      id: 'created',
      label: 'Created',
      content: (
        <DetailRow key="created" label="Created" value={formatDateTime(organization.createdAt)} />
      ),
    },
    {
      id: 'updated',
      label: 'Updated',
      content: (
        <DetailRow key="updated" label="Updated" value={formatDateTime(organization.updatedAt)} />
      ),
    },
  ];

  return (
    <OrganizationDetailPanel
      title="Organization details"
      span="2x2"
      panelId={panelId}
      currentSpan={currentSpan}
      settingsItems={detailItems}
    />
  );
}
